import { useRef } from 'react';
import styles from './SettingsConfigTab.module.css';
import { renderMarkdownToHtml } from '../../../../../shared/widget/markdown';
import { WidgetTokenUsage } from '../../../shared/config';
import { formatTokenCount } from '../../../shared/embed';

export interface SettingsConfigValues {
  sessionTimeout: number;
  language: string;
  title: string;
  greeting: string;
  dataAnalystEnabled: boolean;
  dataAnalystCheckerEnabled: boolean;
}

interface Props {
  config: SettingsConfigValues;
  onChange: (partial: Partial<SettingsConfigValues>) => void;
  tokenUsage: WidgetTokenUsage | null;
  isCreateMode?: boolean;
  isSuperAdmin?: boolean;
  checkerEnabledGlobally?: boolean;
  updateConfig: (partial: Partial<SettingsConfigValues>) => void;
  validationErrors: Partial<Record<keyof SettingsConfigValues, string>>;
}

export default function SettingsConfigTab({ config, tokenUsage, isCreateMode, isSuperAdmin, checkerEnabledGlobally = true, validationErrors, updateConfig }: Props) {
  const greetingRef = useRef<HTMLTextAreaElement>(null);

  const wrapGreetingSelection = (before: string, after: string) => {
    const ta = greetingRef.current;
    if (!ta) return;

    const start = ta.selectionStart;
    const end = ta.selectionEnd;
    const selected = config.greeting.slice(start, end);
    const newText = config.greeting.slice(0, start) + before + selected + after + config.greeting.slice(end);

    updateConfig({ greeting: newText });
    requestAnimationFrame(() => {
      ta.focus();
      const cursorPos = start + before.length + selected.length + after.length;
      ta.setSelectionRange(cursorPos, cursorPos);
    });
  };

  return (
    <>
      {!isCreateMode && tokenUsage && (
        <div className={styles.section}>
          <h3 className={styles.sectionTitle}>Token Usage This Month</h3>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
            <div style={{ background: '#f3f4f6', borderRadius: '0.5rem', height: '1rem', overflow: 'hidden' }}>
              <div
                style={{
                  height: '100%',
                  borderRadius: '0.5rem',
                  width: `${tokenUsage.monthlyLimit > 0 ? Math.min(100, (tokenUsage.totalTokens / tokenUsage.monthlyLimit) * 100) : 0}%`,
                  background: tokenUsage.monthlyLimit > 0 && tokenUsage.totalTokens / tokenUsage.monthlyLimit > 0.85
                    ? '#dc2626'
                    : tokenUsage.monthlyLimit > 0 && tokenUsage.totalTokens / tokenUsage.monthlyLimit > 0.6
                      ? '#f59e0b'
                      : '#22c55e',
                  transition: 'width 0.3s ease',
                }}
              />
            </div>
            <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.8125rem', color: '#374151' }}>
              <span>
                {formatTokenCount(tokenUsage.totalTokens)} / {formatTokenCount(tokenUsage.monthlyLimit)}
                {tokenUsage.monthlyLimit > 0 && ` (${Math.round((tokenUsage.totalTokens / tokenUsage.monthlyLimit) * 100)}%)`}
              </span>
            </div>
            <div style={{ fontSize: '0.75rem', color: '#6b7280' }}>
              Input: {formatTokenCount(tokenUsage.inputTokens + tokenUsage.cacheReadTokens + tokenUsage.cacheWriteTokens)} | Output: {formatTokenCount(tokenUsage.outputTokens)}
            </div>
          </div>
        </div>
      )}

      <div className={styles.section}>
        <h3 className={styles.sectionTitle}>General</h3>
        <div className={styles.field}>
          <label>Session Timeout (minutes)</label>
          <input type="number" min={1} max={1440} value={config.sessionTimeout} onChange={e => updateConfig({ sessionTimeout: parseInt(e.target.value, 10) || 0 })} />
          {validationErrors.sessionTimeout && <span className={styles.fieldError}>{validationErrors.sessionTimeout}</span>}
        </div>
      </div>
      <div className={styles.section}>
        <h3 className={styles.sectionTitle}>Content</h3>
        <div className={styles.field}>
          <label>Language</label>
          <select value={config.language} onChange={e => updateConfig({ language: e.target.value })}>
            <option value="en">English</option>
            <option value="he">Hebrew</option>
          </select>
        </div>
        <div className={styles.field}>
          <label>Title</label>
          <input type="text" value={config.title} onChange={e => updateConfig({ title: e.target.value })} />
        </div>
        <div className={styles.field}>
          <label>Greeting</label>
          <div className={styles.greetingEditor}>
            <div className={styles.greetingToolbar}>
              <button
                type="button"
                className={styles.greetingToolBtn}
                title="Bold"
                onMouseDown={e => { e.preventDefault(); wrapGreetingSelection('**', '**'); }}
              ><strong>B</strong></button>
              <button
                type="button"
                className={styles.greetingToolBtn}
                title="Italic"
                onMouseDown={e => { e.preventDefault(); wrapGreetingSelection('*', '*'); }}
              ><em>I</em></button>
              <button
                type="button"
                className={styles.greetingToolBtn}
                title="Line break"
                onMouseDown={e => { e.preventDefault(); wrapGreetingSelection('\n', ''); }}
              >↵</button>
            </div>
            <textarea
              ref={greetingRef}
              rows={3}
              value={config.greeting}
              onChange={e => updateConfig({ greeting: e.target.value })}
              placeholder="Supports **bold**, *italic*, and line breaks"
            />
            {config.greeting && (
              <div className={styles.greetingPreview}>
                <span className={styles.greetingPreviewLabel}>Preview</span>
                <div dangerouslySetInnerHTML={{ __html: renderMarkdownToHtml(config.greeting) }} />
              </div>
            )}
          </div>
        </div>
      </div>

      {isSuperAdmin && (
        <div className={styles.section}>
          <h3 className={styles.sectionTitle}>Data Analysis</h3>
          <div className={styles.field}>
            <label>Advanced Data Analysis</label>
            <div className={styles.toggleRow}>
              <button
                className={`${styles.toggleBtn} ${config.dataAnalystEnabled ? styles.toggleBtnActive : ''}`}
                onClick={() => updateConfig({ dataAnalystEnabled: !config.dataAnalystEnabled })}
                type="button"
              >
                {config.dataAnalystEnabled ? 'Enabled' : 'Disabled'}
              </button>
              <span className={styles.toggleHint}>
                {config.dataAnalystEnabled
                  ? 'A dedicated analyst agent writes and runs every calculation; intermediate results are saved and reused'
                  : 'The chat agent writes its own SQL over loaded data'}
              </span>
            </div>
          </div>
          {config.dataAnalystEnabled && (
            <div className={styles.field}>
              <label>Requirements Checker</label>
              <div className={styles.toggleRow}>
                <button
                  className={`${styles.toggleBtn} ${config.dataAnalystCheckerEnabled ? styles.toggleBtnActive : ''}`}
                  onClick={() => updateConfig({ dataAnalystCheckerEnabled: !config.dataAnalystCheckerEnabled })}
                  type="button"
                >
                  {config.dataAnalystCheckerEnabled ? 'Enabled' : 'Disabled'}
                </button>
                <span className={styles.toggleHint}>
                  {!checkerEnabledGlobally
                    ? 'Turned off system-wide in Settings > Chat LLM > Data Analyst - this widget\'s switch has no effect until it is on there'
                    : config.dataAnalystCheckerEnabled
                      ? 'Before each analysis an LLM checks the task against the business instructions and the user request'
                      : 'Tasks go straight to the analyst after the table/field check'}
                </span>
              </div>
            </div>
          )}
        </div>
      )}
    </>
  );
}
